import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { WALLET } from '../models/wallet';
import { AuthService } from './auth.service';
import { HttpService } from './http.service';
import { LoggerService } from './logger.service';

const DEPA_FIAT_CURRENCIES = ['EUR', 'GBP', 'USD'];
const CASHIN_SERVICE_NAMES = [
  'cashin',
  'cash in',
  'payin',
  'pay in',
  'entrada',
  'entrada de fondos',
];

interface ModalEvent {
  open: boolean;
  currency: string | null;
  /** Solo proveedores que generan link/QR de pago (Minteo, Bitso, Depa, etc.). */
  paymentLinkOnly?: boolean;
  immediateDebit?: boolean;
  immediateCredit?: boolean;
}

interface ExchangeRateCalculatorEvent {
  open: boolean;
  currency: string | null;
}

interface AddExchangeEvent {
  open: boolean;
  currency: string | null;
}

interface TransactionCreatedEvent {
  type: 'cashin' | 'cashout';
}

interface ViewCardEvent {
  open: boolean;
  card: any;
}

interface newPaymentEvent {
  open: boolean;
  symbol: string | null;
  token: string | null;
  type: string | null;
  amount: any;
  wallet: any;
}

interface viewWalletEvent {
  open: boolean;
  type: string | null;
  amount: any;
  currency: string | null;
  token: any;
}

@Injectable()
export class AppService {
  wallets: WALLET[] = [];
  config: any = null;
  minimized = false;
  private readonly sidebarMinimizedStorageKey =
    'transferswap_sidebar_minimized';

  private addCashoutClicked = new Subject<ModalEvent>();
  addCashoutClicked$ = this.addCashoutClicked.asObservable();

  private transactionCreated = new Subject<TransactionCreatedEvent>();
  transactionCreated$ = this.transactionCreated.asObservable();

  private exchangeCreated = new Subject<null>();
  exchangeCreated$ = this.exchangeCreated.asObservable();

  private viewVirtualCardClicked = new Subject<ViewCardEvent>();
  viewVirtualCardClicked$ = this.viewVirtualCardClicked.asObservable();

  private viewVirtualCardTransactionsClicked = new Subject<ViewCardEvent>();
  viewVirtualCardTransactionsClicked$ =
    this.viewVirtualCardTransactionsClicked.asObservable();

  private addVirtualCardClicked = new Subject<boolean>();
  addVirtualCardClicked$ = this.addVirtualCardClicked.asObservable();

  private refreshVirtualCardsClicked = new Subject<null>();
  refreshVirtualCardsClicked$ = this.refreshVirtualCardsClicked.asObservable();

  private addCashinClicked = new Subject<ModalEvent>();
  addCashinClicked$ = this.addCashinClicked.asObservable();

  private addPaymentClicked = new Subject<newPaymentEvent>();
  addPaymentClicked$ = this.addPaymentClicked.asObservable();

  private viewWalletsClicked = new Subject<viewWalletEvent>();
  viewWalletsClicked$ = this.viewWalletsClicked.asObservable();

  private addInternalClicked = new Subject<ModalEvent>();
  addInternalClicked$ = this.addInternalClicked.asObservable();

  private createSupportTicketClicked = new Subject<boolean>();
  createSupportTicketClicked$ = this.createSupportTicketClicked.asObservable();

  private exchangeRateCalculatorClicked =
    new Subject<ExchangeRateCalculatorEvent>();
  exchangeRateCalculatorClicked$ =
    this.exchangeRateCalculatorClicked.asObservable();

  private addExchangeClicked = new Subject<AddExchangeEvent>();
  addExchangeClicked$ = this.addExchangeClicked.asObservable();

  constructor(
    private http: HttpService,
    private logger: LoggerService,
    private authService: AuthService,
  ) {
    this.minimized = this.readBooleanFromStorage(
      this.sidebarMinimizedStorageKey,
      false,
    );
  }

  setSidebarMinimized(value: boolean) {
    this.minimized = value;
    this.writeBooleanToStorage(this.sidebarMinimizedStorageKey, value);
  }

  triggerViewWallets(
    open: boolean,
    type: string | null = null,
    amount: any = null,
    currency: string | null = null,
    token: any = null,
  ) {
    if (!this.authService.guardTransactionalAction(open)) return;
    this.viewWalletsClicked.next({ open, type, amount, currency, token });
  }

  triggerAddInternal(open: boolean, currency: string | null = null) {
    if (!this.authService.guardTransactionalAction(open)) return;
    this.addInternalClicked.next({ open, currency });
  }

  triggerCreateSupportTicket(open: boolean) {
    this.createSupportTicketClicked.next(open);
  }

  triggerExchangeRateCalculator(open: boolean, currency: string | null = null) {
    this.exchangeRateCalculatorClicked.next({ open, currency });
  }

  triggerAddExchange(open: boolean, currency: string | null = null) {
    if (!this.authService.guardTransactionalAction(open)) return;
    this.addExchangeClicked.next({ open, currency });
  }

  triggerExchangeCreated() {
    this.exchangeCreated.next(null);
  }

  triggerAddCashout(
    open: boolean,
    currency: string | null = null,
    immediateCredit = false,
  ) {
    if (!this.authService.guardTransactionalAction(open)) return;
    this.addCashoutClicked.next({ open, currency, immediateCredit });
  }

  triggerTransactionCreated(type: 'cashin' | 'cashout') {
    this.transactionCreated.next({ type });
  }

  triggerViewVirtualCard(open: boolean, card: any = null) {
    this.viewVirtualCardClicked.next({ open, card });
  }

  triggerViewVirtualCardTransactions(open: boolean, card: any = null) {
    this.viewVirtualCardTransactionsClicked.next({ open, card });
  }

  triggerAddVirtualCard(open: boolean) {
    if (!this.authService.guardTransactionalAction(open)) return;
    this.addVirtualCardClicked.next(open);
  }

  triggerRefreshVirtualCards() {
    this.refreshVirtualCardsClicked.next(null);
  }

  hasDepaCashinProvider(currency: string): boolean {
    const cur = (currency || '').toUpperCase();
    if (!DEPA_FIAT_CURRENCIES.includes(cur)) {
      return false;
    }
    const currencyConfig = (this.config?.currencies ?? []).find(
      (c: any) => String(c?.id ?? c?.currency ?? '').toUpperCase() === cur,
    );
    const cashinService = currencyConfig?.services?.find((s: any) =>
      CASHIN_SERVICE_NAMES.includes(
        String(s?.name ?? '').trim().toLowerCase(),
      ),
    );
    return (cashinService?.provider_services ?? []).some((ps: any) => {
      const slug = String(
        ps?.provider?.metadata?.provider_slug ??
          ps?.metadata?.provider_slug ??
          '',
      )
        .trim()
        .toLowerCase();
      if (
        slug === 'depa' ||
        slug === 'depa_eur' ||
        slug === 'depa_gbp' ||
        slug === 'depa_usd'
      ) {
        return true;
      }
      const username = String(ps?.provider?.username ?? ps?.username ?? '')
        .trim()
        .toLowerCase();
      return username.includes('depa');
    });
  }

  triggerAddCashin(
    open: boolean,
    currency: string | null = null,
    paymentLinkOnly = false,
    immediateDebit = false,
  ) {
    if (!this.authService.guardTransactionalAction(open)) return;
    this.addCashinClicked.next({
      open,
      currency,
      paymentLinkOnly,
      immediateDebit,
    });
  }

  triggerAddPayment(
    open: boolean,
    symbol: string | null = null,
    token: string | null = null,
    type: string | null = null,
    amount: any = null,
    wallet: any = null,
  ) {
    if (!this.authService.guardTransactionalAction(open)) return;
    this.addPaymentClicked.next({ open, symbol, token, type, amount, wallet });
  }

  getConfig() {
    return new Promise((resolve, reject) => {
      this.http.get('config').subscribe(
        (resp: any) => {
          this.config = resp.data;
          resolve(resp);
        },
        (error) => {
          reject(error);
        },
      );
    });
  }

  getWallets() {
    return new Promise((resolve, reject) => {
      this.http.get('wallets').subscribe(
        (resp: any) => {
          this.wallets = resp.data;
          this.logger.log('Wallets:', this.wallets);

          resolve(resp);
        },
        (error) => {
          console.error('Error retrieving wallets:', error);
          reject(error);
        },
      );
    });
  }

  getWalletName = (id: string, currency: string) => {
    if (!this.config?.currencies?.length) return id ?? '-';
    const foundCurrency = this.config.currencies.find(
      (el: any) => el.id === currency,
    );
    if (!foundCurrency?.subwallets?.length) return id ?? '-';
    return (
      foundCurrency.subwallets.find((el: any) => el.id === id)?.shortname ??
      id ??
      '-'
    );
  };

  private readBooleanFromStorage(key: string, fallback: boolean) {
    try {
      const storedValue = localStorage.getItem(key);
      if (storedValue === null) return fallback;
      return storedValue === 'true';
    } catch {
      return fallback;
    }
  }

  private writeBooleanToStorage(key: string, value: boolean) {
    try {
      localStorage.setItem(key, String(value));
    } catch {
      // Ignore storage errors to avoid breaking the UI.
    }
  }
}
